<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { computed, ref, useTemplateRef, getCurrentInstance, onMounted, watch, onBeforeUnmount } from 'vue'
import { useNotification } from "@kyvg/vue3-notification"
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { mediaPulseModel } from '@/components/Composable/mediaPulse'
import _ from 'lodash'
import moment from 'moment'
import Spinner from "@/components/ui/Spinner.vue"
import StreamService from '@/services/StreamService'
import MetricsService from '@/services/MetricsService'
import UserService from '@/services/UserService'
import StreamSchedulingConfigurator from '@/views/SchedulerDash/StreamSchedulingMode.vue'
import StreamOverviewStats from '@/views/StreamsDash/StreamOverviewStats.vue'
import ScheduleStreamPreviewPane from '@/views/StreamsDash/ScheduleStreamPreviewPane.vue'
import ScheduleSetupBar from '@/views/StreamsDash/ScheduleSetupBar.vue'
import AlertModal from '@/components/modals/AlertModal.vue'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import utils from '@/utils'
import PaywallService from '@/services/PaywallService'
import ToggleStreamModal from '@/components/modals/ToggleStreamModal.vue'
import AddStreamModal from '@/components/modals/AddStreamModal.vue'
import IconBase from '../../components/icon/IconBase.vue'
import IconChevronLeft from '../../components/icon/IconChevronLeft.vue'
import BackButton from '@/components/Atoms/BackButton.vue'
import IconBookOpen from '../../components/icon/IconBookOpen.vue'
import IconPlay from '../../components/icon/IconPlay.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import DropDownMenu from '../../components/Atoms/DropDownMenu.vue'
import DropDownMenuItem from '../../components/Atoms/DropDownMenuItem.vue'
import IconBin from '../../components/icon/IconBin.vue'
import StreamSidebar from '../../components/ui/StreamSidebar.vue'
import PlatformList from '@/components/ui/PlatformList.vue'
import Title from '../../components/ui/Title.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import HeaderHelperLinks from '@/components/Moleculas/HeaderHelperLinks.vue'
import Badge from '@/components/Badge.vue'
import { bytes } from '@/filtersNew'
import Alert from '@/components/Atoms/Alert.vue'
import { isLivestreamManageBlocked, useChatOnlyLock } from '@/composables/useChatOnlyLock'
import { track, EVENTS } from '@/lib/tracking'

const instance = getCurrentInstance()
const root = instance.proxy.$root
const route = useRoute()
const router = useRouter()
const store = useStore()
const { notify } = useNotification()

//TemplateRefs
const sidebarStreamPreviewPane = useTemplateRef('sidebarStreamPreviewPane')

// Tab bar horizontal scroll affordance (arrows + fade edges)
const tabBarRef = ref(null)
const tabCanScrollLeft = ref(false)
const tabCanScrollRight = ref(false)
const updateTabScroll = () => {
	const el = tabBarRef.value
	if (!el) return
	tabCanScrollLeft.value = el.scrollLeft > 1
	tabCanScrollRight.value = Math.ceil(el.scrollLeft + el.clientWidth) < el.scrollWidth - 1
}
const scrollTabs = (dir) => {
	const el = tabBarRef.value
	if (!el) return
	el.scrollBy({ left: dir * Math.max(el.clientWidth * 0.6, 160), behavior: 'smooth' })
}

// Click-and-drag to scroll the tab bar (desktop mouse). Touch keeps native
// momentum scrolling, so we only engage drag for the mouse pointer type.
let tabDragActive = false
let tabDragCapturing = false
let tabDragStartX = 0
let tabDragStartScroll = 0
const tabDragMoved = ref(false)
const onTabPointerDown = (e) => {
	const el = tabBarRef.value
	if (!el || e.pointerType !== 'mouse' || e.button !== 0) return
	// Arm a potential drag, but don't capture the pointer yet — capturing on a
	// plain click would retarget the follow-up click away from the tab link and
	// break navigation. We only capture once real movement starts.
	tabDragActive = true
	tabDragCapturing = false
	tabDragMoved.value = false
	tabDragStartX = e.clientX
	tabDragStartScroll = el.scrollLeft
}
const onTabPointerMove = (e) => {
	if (!tabDragActive) return
	const el = tabBarRef.value
	if (!el) return
	const dx = e.clientX - tabDragStartX
	if (!tabDragCapturing) {
		if (Math.abs(dx) <= 3) return
		// Movement threshold crossed: this is a drag, not a click.
		tabDragCapturing = true
		tabDragMoved.value = true
		el.setPointerCapture?.(e.pointerId)
		el.classList.add('tab-dragging')
	}
	el.scrollLeft = tabDragStartScroll - dx
}
const onTabPointerUp = (e) => {
	if (!tabDragActive) return
	tabDragActive = false
	const el = tabBarRef.value
	if (el && tabDragCapturing) {
		el.releasePointerCapture?.(e.pointerId)
		el.classList.remove('tab-dragging')
	}
	tabDragCapturing = false
	// Keep tabDragMoved truthy through the click that immediately follows a
	// drag-release so the click handler can suppress an accidental tab switch,
	// then reset on the next tick.
	setTimeout(() => { tabDragMoved.value = false }, 0)
}
// Suppress the click that fires right after a drag so dragging never navigates.
const onTabClickCapture = (e) => {
	if (tabDragMoved.value) {
		e.preventDefault()
		e.stopPropagation()
	}
}
onMounted(() => {
	updateTabScroll()
	setTimeout(updateTabScroll, 300)
	window.addEventListener('resize', updateTabScroll)
})
onBeforeUnmount(() => {
	window.removeEventListener('resize', updateTabScroll)
})

// Refs
const notAllowed = ref(false)
const isVideoListLoading = ref(false)
const scopeAlive = ref(true)
const statusProcessing = ref(false)
const trialSubscription = ref(null)
const processing = ref(true)
const processingMessage = ref(null)
const stream = ref(null)
const streamId = ref(null)
const streamName = ref(null)
const streamFps = ref(null)
const backupMediaPulse = ref(null)
// const backupStreamAlive = ref(false)
const primaryMediaPulse = ref(null)
// const primaryStreamAlive = ref(false)
const videoFiles = ref([])
const videoFilesLoaded = ref(true)
const userSubscription = ref([])
const isVodPaid = ref(false)
const isVodTrial = ref(false)
const vodTrialExpiredIn = ref(0) // days
const schedulerSettings = ref(null)
// What the scheduled preview pane shows (see scheduledPreviewState), so the setup bar agrees with it.
const schedulePreviewState = ref(null)
const scheduledAlert = ref(true)
const isRtmpPullEnabled = ref(null)
const streamDvrEnabled = ref(null)
const scheduledStorageLimit = ref(null)
const linkedSource = ref(null)
const childViewList = ref([
	// simulcast navigations
	{
		streamType: 'restream',
		name: 'destinations',
		viewName: 'SimulcastManageDashboard'
	},
	{
		streamType: 'restream',
		name: 'Analytics',
		viewName: 'LivestreamManageAnalytics'
	},
	{
		streamType: 'restream',
		name: 'recordings',
		viewName: 'SimulcastManageVodEpisodes'
		// validate(s) { return s.enabled }
	},
	{
		streamType: 'restream',
		name: 'Alerts',
		viewName: 'StreamsManageAlerts'
	},
	{
		streamType: 'live',
		name: 'destinations',
		viewName: 'LivestreamManageDashboard'
	},
	{
		streamType: 'live',
		name: 'Analytics',
		// nameHtml: 'analytics <span class="beta-btn2">Improved<span/>',
		viewName: 'LivestreamManageAnalytics'
	},
	{
		streamType: 'live',
		name: 'Live to VOD',
		viewName: 'LivestreamManageClipEpisodes'
	},
	{
		streamType: 'live',
		name: 'Advertising',
		viewName: 'StreamsManageAdvertising'
	},
	{
		streamType: 'live',
		name: 'Chat',
		viewName: 'LivestreamManageAudience'
	},
	{
		streamType: 'live',
		name: 'Settings',
		viewName: 'LivestreamManageSettings'
	},
	{
		streamType: 'live',
		name: 'Backup',
		viewName: 'StreamsManageSettingsBackup'
	},
	{
		streamType: 'ipcam',
		name: 'dashboard',
		viewName: 'CamstreamManageDashboard'
	},
	{
		streamType: 'ipcam',
		name: 'vod episodes',
		viewName: 'CamstreamManageVodEpisodes'
	},

	// scheduled stream navigations
	{
		streamType: 'scheduled',
		name: 'destinations',
		viewName: 'ScheduledStreamManageDashboard'
	},

	// vod stream navigations
	{
		streamType: 'vod',
		name: 'videos',
		viewName: 'VODBucketVideos'
	},
	{
		streamType: 'vod',
		name: 'settings',
		viewName: 'VODBucketSettings'
	},
	{
		streamType: 'vod',
		name: 'Advertising',
		badge: 'beta',
		viewName: 'StreamsManageVodAdvertising'

	},
	{
		streamType: 'vod',
		name: 'paywall',
		viewName: 'VODstreamManagePaywall'
	},
])
const doc = ref({
	restream: {
		text: 'How multistreaming works',
		link: 'https://docs.castr.com/en/articles/4354481-how-to-start-multistreaming-on-castr',
		iframe: 'https://www.youtube.com/embed/vmq22PzN-HI'
	},
	live: {
		text: 'How live streaming works',
		link: 'https://docs.castr.com/en/articles/4348730-how-to-start-an-all-in-one-stream-in-castr',
		iframe: 'https://www.youtube.com/embed/Txy2k4IAIWI'
	},
	scheduled: {
		text: 'How pre-recorded streaming works',
		link: 'https://docs.castr.com/en/articles/2995226-how-to-start-streaming-pre-recorded-videos',
		iframe: 'https://www.youtube.com/embed/zhe67f3iHwU'
	},
	subsecond: {
		text: 'How sub-second streaming works',
		link: 'https://docs.castr.com/en/articles/8693777-sub-second-streaming',
		iframe: '48ofjE3WZ064-SuIElpIr?utm_source=link'

	}
})
const toggleProcessing = ref(false)

// Media Pulse 
const { subscribeMediaPulse, unsubscribeMediaPulse, mediaPulse, mediaPulsePrimary, mediaPulseBackup } = mediaPulseModel()

// Computed

const baseSubscription = computed(() => store.getters['User/baseSubscription'])
const addonSubscriptions = computed(() => store.getters['User/addonSubscriptions'])
const userEmail = computed(() => store.getters['User/userEmail'])
const userLoaded = computed(() => store.getters['User/userLoaded'])
const activeTeam = computed(() => store.getters['User/activeTeam'])
const userPermissions = computed(() => store.getters['User/userPermissions'])
const streamAlive = computed(() => !!(stream.value.enabled && mediaPulse.value?.alive))
const backupStreamAlive = computed(() => !!(stream.value.enabled && mediaPulseBackup.value?.alive))
const primaryStreamAlive = computed(() => !!(stream.value.enabled && mediaPulsePrimary.value?.alive))

const paywallFeature = computed(() => {
	const user = UserService.getUser(activeTeam.value)
	if (user.paywallFeature) {
		return true
	}
	if (stream.value.type === 'live' && baseSub.value) {
		return _.get(baseSub.value, ['package', 'definition', 'paywall'], false)
	}

	return false
})

const streamType = computed(() => stream.value?.type)

const childViews = computed(() => {
	return (stream.value && childViewList.value.filter(v => {
		if (v.name === 'paywall' && !paywallFeature.value) {
			return false
		}

		const isIncluded = v.streamType === stream.value.type
		return isIncluded && v.validate ? v.validate(stream.value) : isIncluded
	}))
})

const trialSubscriptionPromptMessage = computed(() => {
	let remainingDays = 7
	if (baseSubscription.value) {
		const subEndTime = new Date(baseSubscription.value.cend)
		let diff = subEndTime - new Date()
		if (_.isNumber(diff)) {
			diff /= 24 * 3600 * 1000
			diff = Math.floor(diff)
			remainingDays = diff
		}
	}
	let msg = `Your trial plan will end in ${remainingDays || 7} days.`
	if (remainingDays <= 0) {
		msg = `Your trial period has expired.`
	}
	return msg
})

const baseSub = computed(() => {
	return (addonSubscriptions?.value ?? []).find(sub => sub.category && sub.category.includes(stream.value.type))
})

const tooltipText = computed(() => {
	return `
		<span class="text-dark-8 text-small">
			You don't have permission. Contact the team owner at <a class="text-dark-8 text-small"
			:href="'mailto:${userEmail.value}'">${userEmail.value}</a> to gain access.
		</span>`
})

// "Moderate Chat only": every surface but the Chat tab's Live room renders
// disabled. Its tooltip copy is deliberately NOT `tooltipText` above.
const { locked: chatOnlyLocked, tooltip: chatOnlyTooltip, isLockedRoute } = useChatOnlyLock(
	computed(() => streamType.value === 'live'),
)
const isTabLocked = (viewItem) => isLockedRoute(viewItem?.viewName)
// The router guard runs before `User/getactiveTeam` resolves on a cold load, so
// it deliberately lets a locked URL through rather than bouncing owners. This
// catches up once permissions arrive, which is what makes a bookmarked
// /livestreams/{id}/settings land on the Live room after a hard reload.
watch(chatOnlyLocked, (locked) => {
	if (locked && isLockedRoute(route.name)) {
		router.replace({ name: 'LivestreamManageAudienceLiveRoom', params: route.params })
	}
}, { immediate: true })

const manageBlocked = computed(() => streamType.value === 'live' && isLivestreamManageBlocked(store))
watch(manageBlocked, (blocked) => {
	if (blocked) router.replace({ name: 'ChannelList' })
}, { immediate: true })

// Drop the hover affordance when the title is not editable, so it doesn't invite
// a click that does nothing.
const streamTitleClasses = computed(() => [
	'-mx-2 px-[0.5rem] rounded-md line-clamp-2 text-lg font-semibold min-h-8 min-w-0 md:leading-8',
	chatOnlyLocked.value ? '' : 'hover:bg-surface-3',
].join(' ').trim())
// The chat-only lock wins the tooltip: it is the reason the control is disabled.
const actionTooltipHtml = computed(() => chatOnlyLocked.value ? chatOnlyTooltip : tooltipText.value)

const tooltipLinkedSourceText = computed(() => {
	const linkedStreamName = enabledLinkedSource.value?.streamObject?.name ?? ''
	return `This livestream cannot be deleted because it is being used as a source in ${linkedStreamName} livestream. To delete this livestream, first remove it as a source from ${linkedStreamName} livestream.`
})

const hasEnabledLinkedSources = computed(() => {
	return stream.value?.platforms?.some(platform => platform.enabled && platform.linkedSource) || false
})

const enabledLinkedSource = computed(() => {
	return stream.value?.platforms?.find(platform => platform.enabled && platform.linkedSource && platform.streamObject)
})

// Check if this stream is being used as a linked source destination by other streams
const isUsedAsLinkedSourceDestination = computed(() => {
	if (!stream.value?._id) return false
	const allStreams = store.getters['Streams/streams'] || []
	// Check if any other stream has a platform with linkedSource=true pointing to this stream
	return allStreams.some(otherStream => {
		if (!otherStream.platforms || otherStream._id === stream.value._id) return false
		return otherStream.platforms.some(platform => 
			platform.enabled && 
			platform.linkedSource && 
			platform.streamObject && 
			(platform.streamObject._id === stream.value._id || platform.streamObject === stream.value._id)
		)
	})
})

// Find which streams are using this stream as a linked source destination
const streamsUsingThisAsDestination = computed(() => {
	if (!stream.value?._id) return []
	const allStreams = store.getters['Streams/streams'] || []
	return allStreams.filter(otherStream => {
		if (!otherStream.platforms || otherStream._id === stream.value._id) return false
		return otherStream.platforms.some(platform => 
			platform.enabled && 
			platform.linkedSource && 
			platform.streamObject && 
			(platform.streamObject._id === stream.value._id || platform.streamObject === stream.value._id)
		)
	})
})

const tooltipUsedAsDestinationText = computed(() => {
	const streamNames = streamsUsingThisAsDestination.value.map(s => s.name).join(', ')
	return `This livestream is currently used as a linked source destination in ${streamNames} livestream(s). Remove it from those livestreams to proceed with deletion.`
})

const loadScheduledStorageLimit = async () => {
	if (!stream.value || stream.value.type !== 'scheduled') return
	let maxSize = null
	const activeScheduledSubs = _.filter(_.cloneDeep(addonSubscriptions.value), (sub) => sub.category === 'scheduled' && sub.enabled === true)
	let activeScheduledSub = null
	activeScheduledSubs.forEach(sub => {
		try {
			if (sub.definitionOverride && sub.definitionOverride.schedulerMaxVideoSize) {
				sub.package.definition.schedulerMaxVideoSize = parseFloat(sub.definitionOverride.schedulerMaxVideoSize)
			}
			if (maxSize) {
				const tmpMaxSize = parseFloat(sub.package.definition.schedulerMaxVideoSize)
				if (tmpMaxSize > maxSize) {
					maxSize = tmpMaxSize
					activeScheduledSub = sub
				}
			} else {
				maxSize = parseFloat(sub.package.definition.schedulerMaxVideoSize)
				activeScheduledSub = sub
			}
		} catch (error) {
			console.error(error)
		}
	})
	try {
		const res = activeScheduledSubs.length > 0
			? await MetricsService.getSubscriptionStorage(UserService.getUserId(activeTeam.value), activeScheduledSub.package._id)
			: {}
		scheduledStorageLimit.value = res ? res : {}
	} catch (error) {
		console.error(error)
		scheduledStorageLimit.value = {}
	}
}

// Hooks
onMounted(async () => {
	await store.dispatch('User/getactiveTeam', null, { root: true })
	streamId.value = route.params.streamId

	// get stream details from already-loaded store
	await setupStream()

	if (!stream.value) return
	if (localStorage.getItem('notAllowed' + stream.value.type)) {
		notAllowed.value = (localStorage.getItem('notAllowed' + stream.value.type) == 'true')
	}
	if ((streamType.value === 'scheduled') && localStorage.getItem('notAllowedscheduled')) {
		notAllowed.value = (localStorage.getItem('notAllowedscheduled') == 'true')
	}

	streamDvrEnabled.value = stream.value.metadata.dvrArchive

	// we can't just update region to new one due HLS binded to region
	// we do hack with multiple regions: region & uiRegion assigned for single stream
	if (!stream.value.uiRegion) {
		stream.value.uiRegion = stream.value.region
	}

	await loadVOD()
	// event tracking
	window.trackEvent('Page Viewed', {
		page: 'Stream',
		streamId: stream.value._id,
		streamType: stream.value.type,
	})

	if (baseSub.value) {
		trialSubscription.value = /trial/gi.test(baseSub.value.package.name)
	}

	if (streamType.value === 'scheduled') {
		videoFilesLoaded.value = false
		loadScheduledStorageLimit()
		fetchStreamVideoFileList()
	}
	processing.value = false
})

onBeforeUnmount(() => {
	scopeAlive.value = false
	unsubscribeMediaPulse()
})

// Watchers
watch(activeTeam, () => {
	loadVOD()
})

watch(() => stream.value?._id, () => {
	linkedSource.value = null
})

// Methods
const dvrRecordingToggle = (status) => {
	// The "Off" Badge in the tab nav reacts to streamDvrEnabled directly
	streamDvrEnabled.value = status
}

const checkDelPermission = () => {
	let permission = userPermissions.value && (
		((stream.value.type === 'live') && !userPermissions.value.liveStreamDelete) ||
		((stream.value.type === 'restream') && !userPermissions.value.recordedStreamDelete) ||
		((stream.value.type === 'scheduled') && !userPermissions.value.preRecordedStreamDelete)
	)
	return permission
}

const checkTogglePermission = () => {
	let permission = userPermissions.value && (
		((stream.value.type === 'live') && !userPermissions.value.liveStreamToggle) ||
		((stream.value.type === 'restream') && !userPermissions.value.recordedStreamToggle) ||
		((stream.value.type === 'scheduled') && !userPermissions.value.preRecordedStreamToggle))
	return permission
}
const loadVOD = async() => {
	if (!stream.value) return
	const userSub = { subscription: baseSubscription.value, addonSubscriptions: addonSubscriptions.value }
	getRmtpPullPermission(userSub)

	if (stream.value) {
		await subscribeMediaPulse(stream.value, true)
	} else {
		userSubscription.value = userSub
		getVodSubExpiredDays()
	}
}

const getRmtpPullPermission = (userSubscription) => {
	try {
		// select the correct package from base + additional packages
		const packages = []

		// base subscription
		if (userSubscription?.subscription) {
			packages.push(userSubscription.subscription.package)
		}
		// add addon packages
		if (userSubscription?.addonSubscriptions?.length) {
			packages.push(...userSubscription.addonSubscriptions.map(sub => sub.package))
		}

		const packageForCurrentStream = packages.find(pack => pack.category === stream.value.type)
		// set the default value to 'true'
		// if the user has the correct package with correct permission, but the `enableRtmp` is not defined
		const userSubDefinition = packageForCurrentStream.definition
		isRtmpPullEnabled.value = 'enableRtmp' in userSubDefinition ? userSubDefinition.enableRtmp : true
	} catch {
		// under any other cicumstance, disable RTMP pull
		isRtmpPullEnabled.value = false
	}
}

const liveStreamCountdownSelected = (d) => {
	let val = d
	if (d) {
		val = Math.max(new Date(d) - Date.now(), 0)
	}
	sidebarStreamPreviewPane.value.streamCountdownSecLeft = val
	sidebarStreamPreviewPane.value.countdownDate = d
}

const liveStreamAbrChanged = (d) => {
	sidebarStreamPreviewPane.value.streamAbrToggleState = d
	if (stream.value?.metadata) stream.value.metadata.abr = d
}

const openCreatestream = () => {
	showModal('modal-add-stream')
}

const onStreamUpdates = (updates) => {
	if (updates == null) return
	if (Array.isArray(updates)) {
		updates = { platforms: updates }
	}
	if (updates.config) {
		stream.value.config.isPasswordDisabled = updates.config.isPasswordDisabled
	} else if (updates.metadata) {
		stream.value = {
			...stream.value,
			metadata: { ...(stream.value.metadata || {}), ...updates.metadata },
		}
	} else {
		stream.value = _.assign({}, stream.value, updates)
	}
}

const setupStream = async () => {
	// get stream details
	try {
		// Wait for streams to be loaded (App.vue is loading them)
		await store.dispatch('Streams/getStreams') // This will wait if loading, or skip if loaded
		
		// Get stream from store (now definitely loaded)
		const streamData = store.getters['Streams/getStreamById'](streamId.value)
		
		if (!streamData) {
			throw new Error('Stream not found')
		}
		
		// Clone to prevent Vuex mutation errors
		stream.value = _.cloneDeep(streamData)
		
		// Fetch metadata using cached store (prevents duplicates)
		const meta = await store.dispatch('StreamCache/getMetadata', { 
			streamId: stream.value._id,
			videoId: false 
		})
		stream.value.metadata = meta
		streamName.value = stream.value.name
	} catch (err) {
		// Redirect to Dashboard when stream is invalid, not found, or belongs to another account
		console.log(err)
		processing.value = false
		notify({
			group: 'error',
			title: err?.error || 'Stream unavailable',
			text: err?.message || 'This stream was not found or you do not have access to it.'
		})
		await router.replace('/dashboard')
		return
	}
}
const onNewStream = async (stream, regionDetails) => {
	notify({ group: 'success', text: 'Stream deployed successfully' })

	let redirectPath = '/multistreams/'
	if (stream.type === 'live') {
		redirectPath = '/livestreams/'
	} else if (stream.type === 'scheduled') {
		redirectPath = '/scheduled/'
	}

	redirectPath += stream._id

	await router.push({ path: redirectPath })
	window.location.reload()
}

const fetchStreamDetails = async (cb) => {
	// Refresh streams from server (used for explicit refresh/updates)
	await store.dispatch('Streams/getStreams', true) // force refresh
	
	// Get updated stream from store and clone to prevent mutation errors
	stream.value = _.cloneDeep(store.getters['Streams/getStreamById'](streamId.value))
	
	if (cb && cb.constructor === Function) {
		cb()
	}
	return stream.value
}

// Enabling/disabling backup changes which ingest pulses must be polled, so
// re-subscribe after refreshing so primary/backup stats start (or stop)
// immediately without a full page reload.
const onBackupToggled = async () => {
	await fetchStreamDetails()
	unsubscribeMediaPulse()
	if (stream.value) await subscribeMediaPulse(stream.value, true)
}

const onStreamNameChange = async (newName) => {
	if (!newName) return

	const previousName = stream.value.name
	if (previousName === newName) return
	streamName.value = newName
	// try changing stream name
	try {
		await StreamService.setStreamName(streamId.value, newName)

		// Keep the Streams store (and this view's local clone) in sync so the
		// dashboard cards — and any other view reading from the store — reflect
		// the new name immediately, without requiring a full page reload.
		store.dispatch('Streams/patchStream', {
			streamId: streamId.value,
			patch: { name: newName },
		})
		stream.value.name = newName

		PaywallService.updateStreamPaywall(streamId.value, { title: newName }).catch(err => {
			console.error('update paywall stream name failed: ', err)
		})

		// track event
		window.trackEvent('Stream Renamed', {
			streamId: streamId.value,
			oldName: previousName,
			newName,
		})
	} catch (err) {
		streamName.value = previousName
		notify({
			group: 'error',
			title: "Couldn't change stream name",
			text: err.message
		})
	}
}

const toggleStatus = async (ev, newStatus) => {
	ev && ev.preventDefault()
	ev && ev.stopPropagation()
	if (stream.value.type === 'scheduled' && stream.value.enabled && schedulerSettings.value.mode == 'datetime') {
		let offset = schedulerSettings.value.datetime - Date.now()
		offset /= 1000
		if (scheduledAlert.value && !(offset <= 0)) {
			showModal('scheduled-status-warning')
			return
		}
	}

	const oldStatus = stream.value.enabled
	newStatus = newStatus === undefined ? !oldStatus : newStatus

	stream.value.enabled = newStatus
	if (!newStatus) {
		scheduledAlert.value = true
	}
	try {
		await StreamService.toggleStream(stream.value._id)

		track(newStatus ? EVENTS.STREAM_ENABLED : EVENTS.STREAM_DISABLED, {
			streamType: stream.value.type
		})
	} catch (err) {
		stream.value.enabled = oldStatus

		notify({
			group: 'error',
			title: "Couldn't toggle stream status",
			text: err.message
		})

		if (err.message && err.message.indexOf('upgrade') > -1) {
			showModal('billing-prompt')
		}
	}

	statusProcessing.value = false
}

const onToggleProcessing = (status) => {
	toggleProcessing.value = status
}

const onToggleDone = () => {
	fetchStreamDetails()
}

const requestStreamDelete = () => {
	if (activeTeam.value && checkDelPermission()) return
	if (chatOnlyLocked.value) return
	
	// Check if this stream is being used as a linked source destination
	if (isUsedAsLinkedSourceDestination.value) {
		notify({
			group: 'error',
			title: 'Cannot delete stream',
			text: tooltipUsedAsDestinationText.value
		})
		return
	}
	
	if (!activeTeam.value || !(((stream.value.type === 'live') && userPermissions.value && !userPermissions.value.liveStreamDelete) || ((stream.value.type === 'restream') && userPermissions.value && !userPermissions.value.recordedStreamDelete))) {
		showModal('modal-confirm')
	}
}

const onStreamDeleteConfirm = async () => {
	processing.value = true
	processingMessage.value = 'Deleting stream'

	let id = streamId.value
	const pseudoVodStream = stream.value.liveParent ? true : false
	if (pseudoVodStream) {
		id = stream.value.liveParent
	}

	// Try to delete its dvr
	let vodEpisodes
	let permanentDvrArchives
	try {
		vodEpisodes = await StreamService.getStreamDvrArchives(id)
	} catch (e) {
		if (!pseudoVodStream) {
			notify({
				group: 'error',
				text: 'could not retrieve saved vod(s)'
			})
		}
	}

	vodEpisodes = _.map(vodEpisodes, vodEpisode => {
		const ep = _.assign({}, vodEpisode, {
			startTimeDate: new Date(vodEpisode.startTime),
			endTimeDate: new Date(vodEpisode.endTime)
		})

		ep.fileName = `${moment(new Date(String(ep.startTimeDate))).fromNow()}`
		ep.fileName = ep.fileName.replace('a few seconds ago', 'a seconds ago')
		ep.creationTime = ep.startTimeDate

		ep.live = false
		ep.expanded = false
		ep.streamType = stream.value.type
		ep.blocking = false
		ep.size = ep.size ? bytes(ep.size, false, 2) : '--'

		ep.exportProcessing = false
		ep.exportStatusTimer = null

		return ep
	})

	permanentDvrArchives = _.filter(vodEpisodes, { exportStatus: 'EXPORTED' }) || []
	permanentDvrArchives.forEach(async (video) => {
		const q = {
			streamId: id,
			from: video.from,
			duration: video.duration,
			abr: video.abr,
			namedHls: video.namedHls
		}
		if (pseudoVodStream || !video.pseudoVod) {
			await StreamService.deleteStreamDvrArchive(q)
		}
	})

	// try deleting stream
	try {
		await StreamService.deleteStream(stream.value._id)
		// Force refresh to remove deleted stream from store
		await store.dispatch('Streams/getStreams', true)
		
		// track event
		window.trackEvent('Stream Deleted', {
			streamId: stream.value._id,
			streamName: stream.value.name,
			streamType: stream.value.type,
		})
		// Navigate away and keep the loading state on until the route changes.
		// Clearing `processing` here would remount this page against the now-deleted
		// stream, firing failed playlist fetches ("Couldn't retrieve the playlist videos").
		if (stream.value.type == 'scheduled') {
			await router.push({ name: 'Scheduler' })
		} else {
			await router.push({ name: 'ChannelList' })
		}
		return
	} catch (err) {
		console.log(err)
		// redirect to stream list
		notify({
			group: 'error',
			title: err.error,
			text: err.message
		})
	}

	processing.value = false
}

const fetchStreamVideoFileList = async () => {
	let list = []
	try {
		list = await StreamService.getStreamPlaylist(stream.value._id)
	} catch (e) {
		notify({
			group: 'error',
			text: 'could not retrieve playlist videos'
		})
	} finally {
		videoFilesLoaded.value = true
	}
	if (prepareVideoFileItem()) list = _.compact(list).map(prepareVideoFileItem())

	videoFiles.value = list
}

const prepareVideoFileItem = (videoFile) => {
	if (videoFile) {
		videoFile.expanded = false
		videoFile.removing = false
		videoFile.statusProcessing = false
		videoFile.uploadTime = new Date(videoFile.uploadTime)
	}
	return videoFile
}

const onVideoFileList = (videoFileList) => {
	if (videoFileList) {
		const list = _.compact(videoFileList).map(video => prepareVideoFileItem(video))
		videoFiles.value = list
		videoFilesLoaded.value = true
	}
}

const onVideoFileUpdate = (videoId, updates) => {
	const vindex = _.findIndex(videoFiles.value, { id: videoId })
	if (vindex > -1) {
		const videoItem = _.assign({}, videoFiles.value[vindex], updates)
		videoFiles.value = utils.updateArrayItem(
			videoFiles.value,
			videoItem,
			vindex
		)
	}
}

const onVideoFileRemoved = (videoId) => {
	const vindex = _.findIndex(videoFiles.value, { id: videoId })
	videoFiles.value = utils.removeArrayItem(videoFiles.value, vindex)
}

const onSchedulerSettingsUpdated = (updatedConfig) => {
	schedulerSettings.value = updatedConfig
}

const getVodSubExpiredDays = () => {
	let originalVodSub
	let paidSub
	let trialSub
	let hasMaxVodItems = false
	if (_.isArray(userSubscription.value)) {
		userSubscription.value.forEach((sub) => {
			if (sub.package.definition.maxVodItems && parseInt(sub.package.definition.maxVodItems, 10) > 0) {
				paidSub = sub
				if (sub.enabled === true) hasMaxVodItems = true
			}
		})
	}
	if (_.isObject(userSubscription.value)) {
		userSubscription.value.addonSubscriptions.forEach((sub) => {
			if (sub.package.definition.maxVodItems && parseInt(sub.package.definition.maxVodItems, 10) > 0) {
				paidSub = sub
				if (sub.enabled === true) hasMaxVodItems = true
			}
		})
	}

	if (paidSub) {
		isVodPaid.value = true
		let paidSubExpiredIn = moment(paidSub.cend).diff(moment(), 'day')


		vodTrialExpiredIn.value = paidSubExpiredIn

		if (hasMaxVodItems) {
			isVodTrial.value = false
			vodTrialExpiredIn.value = 1
		}
	}

	if (originalVodSub) {
		isVodPaid.value = true
		const originalVodSubExpiredIn = moment(originalVodSub.cend).diff(moment(), 'day')

		if (originalVodSubExpiredIn > vodTrialExpiredIn.value) {
			vodTrialExpiredIn.value = originalVodSubExpiredIn
			isVodTrial.value = false
		}
	}
	return vodTrialExpiredIn.value
}

const gotoUpgrade = () => {
	window.location.href = `/app/subscribe?category=live`
}

const checkActivems = () => {
	return !!baseSubscription.value?.enabled
}

const showStreamToggleModal = (ev) => {
	if (chatOnlyLocked.value) return
	if (!checkActivems() && stream.value.type == 'restream' && !stream.value.enabled) {
		showModal('create-aio')
		return
	}
	if (notAllowed.value && !stream.value.enabled) {
		showModal('increase-limit')
	}

	if (activeTeam.value && checkTogglePermission()) return
	if (!notAllowed.value || (notAllowed.value && stream.value.enabled)) {
		showModal('modalToggleStream' + stream.value._id, '#btnShow')
	}
}

function composePlatformPushUrl(platform) {
	if (!platform) return
	let pushUrl = platform.server
	pushUrl = pushUrl.replace(/\/$/g, '')
	pushUrl += '/' + platform.key
	return pushUrl
}
</script>

<template>
	<div class="w-full container detail-shell px-4 mt-4 lg:pt-8 lg:self-start">
		<div v-if="processing" class="flex items-center justify-center w-full min-h-[70vh]" aria-busy="true">
			<Spinner classes="text-dark-8" spinner-color="var(--c-dark-8)" spinner-size="22px" />
		</div>
		<div
			class="main-content flex flex-wrap lg:self-start grid-rows-[min-content_1fr] auto-cols-max"
			:class="[streamType + '_wrap', streamType === 'scheduled'
				? 'lg:grid lg:grid-cols-[minmax(0,1fr)_420px] gap-x-8 lg:gap-x-4'
				: 'xl:grid xl:grid-cols-[calc(100%-420px-1rem)_420px] xl:gap-x-4']"
			v-else>
			<div :class="[streamType === 'scheduled' ? 'col-span-2' : 'col-start-1', 'row-start-1 w-full mb-3 md:mb-4']">
				<div class="flex items-center w-full gap-x-4">
					<BackButton :to="streamType === 'scheduled' ? '/scheduler' : '/dashboard'" class="!hidden md:!inline-flex" />
					<!-- No `wrap-trigger`: the title is an <h2>, not a disabled control, so it
					     receives hover on its own and needs no extra wrapper to truncate in. -->
					<Tooltip :active="chatOnlyLocked" :content="chatOnlyTooltip" wrapper-class="inline-flex min-w-0">
						<Title :editable="!chatOnlyLocked"
							:titleClasses="streamTitleClasses"
							edit-class="bg-surface-3 line-clamp-none" :title="streamName" @changed="onStreamNameChange" />
					</Tooltip>
					<HeaderHelperLinks v-if="streamType !== 'scheduled'" :class="'ml-auto !hidden md:!flex'" :howItWorksLink="doc[stream.type].link" :howItWorksText="doc[stream.type].text" :tutorialLink="doc[stream.type].iframe" />
					<!-- Scheduled: primary stream actions live in the header command bar -->
					<div v-if="streamType === 'scheduled'" class="ml-auto !hidden md:!flex items-center gap-x-2 shrink-0" :class="{ 'pe-none': !userLoaded }">
						<span v-if="statusProcessing" class="inline-block size-4 rounded-full border-2 border-surface-6 border-t-transparent animate-spin"></span>
						<template v-else-if="userPermissions">
							<Tooltip :active="activeTeam && checkTogglePermission()">
								<FormButton @click="showStreamToggleModal" type="secondary"
									:disabled="(activeTeam && checkTogglePermission()) || toggleProcessing"
									:label="stream.enabled ? 'Disable' : 'Enable'" />
								<template #content><span v-html="tooltipText" /></template>
							</Tooltip>
							<Tooltip :active="activeTeam && checkDelPermission() || isUsedAsLinkedSourceDestination">
								<form-button isIcon title="Delete stream" type="danger"
									:disabled="(activeTeam && checkDelPermission()) || isUsedAsLinkedSourceDestination"
									@click="requestStreamDelete">
									<icon-base fill="none"><icon-bin /></icon-base>
								</form-button>
								<template #content><span v-html="isUsedAsLinkedSourceDestination ? tooltipUsedAsDestinationText : tooltipText" /></template>
							</Tooltip>
						</template>
						<template v-else>
							<FormButton type="secondary" :label="stream.enabled ? 'Disable' : 'Enable'" :disabled="toggleProcessing"
								@click="showStreamToggleModal" />
							<Tooltip :content="isUsedAsLinkedSourceDestination ? tooltipUsedAsDestinationText : tooltipLinkedSourceText"
								:active="hasEnabledLinkedSources || isUsedAsLinkedSourceDestination">
								<form-button isIcon title="Delete stream" type="danger" @click="requestStreamDelete"
									:disabled="hasEnabledLinkedSources || isUsedAsLinkedSourceDestination">
									<icon-base fill="none"><icon-bin /></icon-base>
								</form-button>
							</Tooltip>
						</template>
					</div>
					<drop-down-menu isIcon position="right" type="secondary" no-border class="md:hidden ml-auto">
						<template #toggle-button>
							<img src="@/assets/icons/settings.svg" alt="Settings">
						</template>
						<template #menu-items>
								<Tooltip :active="chatOnlyLocked" :content="chatOnlyTooltip" wrapper-class="block w-full" :wrap-trigger="chatOnlyLocked">
									<drop-down-menu-item @click="showStreamToggleModal" :disabled="(activeTeam && checkTogglePermission()) || chatOnlyLocked">
										<span v-if="stream.enabled">Disable</span>
										<span v-else>Enable</span>
									</drop-down-menu-item>
								</Tooltip>
								<Tooltip :active="chatOnlyLocked" :content="chatOnlyTooltip" wrapper-class="block w-full" :wrap-trigger="chatOnlyLocked">
									<drop-down-menu-item :disabled="(activeTeam && checkDelPermission()) || hasEnabledLinkedSources || isUsedAsLinkedSourceDestination || chatOnlyLocked" @click="requestStreamDelete">
										Delete
									</drop-down-menu-item>
								</Tooltip>
							</template>
					</drop-down-menu>
				</div>
				<HeaderHelperLinks v-if="streamType !== 'scheduled'" class="block md:hidden w-full" :howItWorksLink="doc[stream.type].link" :howItWorksText="doc[stream.type].text" :tutorialLink="doc[stream.type].iframe" />
			</div>
			<main :class="streamType === 'scheduled' ? 'order-2 md:order-none col-start-1 row-start-2 w-full min-w-0' : 'order-1 md:order-none col-start-1 row-start-2 w-full min-w-0'">
				<!-- Encoder stat chips only carry live data, so on the scheduled page
				     they'd just show "—" while editing. Show a guided setup + summary
				     bar instead while not live, and the real stats once live. -->
				<schedule-setup-bar v-if="streamType === 'scheduled' && !streamAlive" :video-files="videoFiles"
					:video-files-loaded="videoFilesLoaded" :platforms="stream.platforms" :scheduler-settings="schedulerSettings"
					:enabled="!!stream.enabled" :preview-state="schedulePreviewState" />
				<stream-overview-stats v-if="streamType !== 'scheduled' || streamAlive" :stream="stream" :media-pulse="mediaPulse" :subscription="baseSubscription"
					:video-files="videoFiles" />

				<!-- Scheduled streams: player + playlist in main area -->
				<div v-if="streamType === 'scheduled'" class="mt-4">
					<schedule-stream-preview-pane v-if="schedulerSettings" :stream="stream"
						:stream-alive="streamAlive" :media-pulse="mediaPulse" :scheduler-settings="schedulerSettings"
						:toggle-processing="toggleProcessing" :scheduledStorageLimit="scheduledStorageLimit"
						@video-files="onVideoFileList" @preview-state="schedulePreviewState = $event" layout="main" />
				</div>

				<!-- Non-scheduled streams: tabs + router-view -->
				<div v-else class="bg-surface-2 rounded-xl shadow-[0_2px_6px_rgba(0,0,0,0.28)] p-2 md:p-5">
					<div v-if="streamType !== 'subsecond'" class="relative mb-3 md:mb-4">
						<!-- Scroll-left affordance -->
						<FormButton v-show="tabCanScrollLeft" @click="scrollTabs(-1)" aria-label="Scroll tabs left"
							custom-classes="absolute left-0 top-1/2 -translate-y-1/2 z-20 size-7 grid place-items-center rounded-full bg-surface-3 ring-1 ring-surface-4 text-surface-8 hover:text-white hover:bg-surface-4 shadow-md transition-colors">
							<icon-base fill="none">
								<icon-chevron-left />
							</icon-base>
						</FormButton>
						<div v-show="tabCanScrollLeft"
							class="pointer-events-none absolute left-0 top-0 bottom-0 w-10 z-10 rounded-l-lg bg-gradient-to-r from-surface-1 to-transparent">
						</div>

						<div ref="tabBarRef" @scroll="updateTabScroll"
							@pointerdown="onTabPointerDown" @pointermove="onTabPointerMove"
							@pointerup="onTabPointerUp" @pointercancel="onTabPointerUp"
							@click.capture="onTabClickCapture" @dragstart.prevent
							class="inline-flex items-center gap-1 bg-surface-1 rounded-lg p-1 overflow-x-auto max-w-full no-scrollbar"
							:class="[streamType === 'vod' ? 'vod-nav' : '', { 'tab-draggable': tabCanScrollLeft || tabCanScrollRight }]">
							<!-- `inline-flex` wrapper: the tab bar is an inline-flex row with gap-1,
							     so the wrapper becomes the flex item the router-link used to be and
							     layout is unchanged. It must generate a box — floating-vue measures
							     it to position the popper. -->
							<Tooltip v-for="(viewItem, $index) in childViews" :key="$index"
								:active="isTabLocked(viewItem)" :content="chatOnlyTooltip"
								wrapper-class="inline-flex" wrap-trigger>
								<!-- A locked tab keeps its real `to`: pointing it at `{}` would resolve
								     to the current route and hand it `active-class`, so every locked tab
								     would render as the selected one. `pointer-events-none` only stops
								     the mouse, so `tabindex="-1"` takes it out of the tab order too, and
								     the router guard catches anything that still gets through. -->
								<router-link
									:to="viewItem.viewName ? { name: viewItem.viewName } : {}" draggable="false"
								class="capitalize text-sm font-medium text-surface-8 hover:text-white hover:no-underline hover:bg-surface-3 px-3 py-1.5 rounded-md flex items-center text-nowrap transition-all select-none"
								:class="[viewItem.className || null, isTabLocked(viewItem) ? 'opacity-50 pointer-events-none' : null]"
								:tabindex="isTabLocked(viewItem) ? -1 : undefined"
								:aria-disabled="isTabLocked(viewItem) || undefined"
								active-class="!bg-surface-4 !text-white shadow-sm">
									<span v-if="viewItem.name">
										{{ viewItem.name }}
									</span>
									<span v-else-if="viewItem.nameHtml" v-html="viewItem.nameHtml" />
									<Badge v-if="viewItem.badge" type="main" size="xs" class="ml-1 uppercase">{{ viewItem.badge }}</Badge>
									<Badge v-if="viewItem.viewName === 'LivestreamManageClipEpisodes' && !streamDvrEnabled"
										size="xs" pill title="Off"
										class="ml-1.5 uppercase tracking-wide !bg-surface-3 !text-surface-7 ring-1 ring-inset ring-white/[0.06]" />
								</router-link>
							</Tooltip>
						</div>

						<!-- Scroll-right affordance -->
						<div v-show="tabCanScrollRight"
							class="pointer-events-none absolute right-0 top-0 bottom-0 w-10 z-10 rounded-r-lg bg-gradient-to-l from-surface-1 to-transparent">
						</div>
						<FormButton v-show="tabCanScrollRight" @click="scrollTabs(1)" aria-label="Scroll tabs right"
							custom-classes="absolute right-0 top-1/2 -translate-y-1/2 z-20 size-7 grid place-items-center rounded-full bg-surface-3 ring-1 ring-surface-4 text-surface-8 hover:text-white hover:bg-surface-4 shadow-md transition-colors">
							<icon-base fill="none" class="rotate-180">
								<icon-chevron-left />
							</icon-base>
						</FormButton>
					</div>
					<div class="tab-content relative mt-3 md:mt-4">
						<Alert v-if="trialSubscription && route.name !== 'LivestreamManagePaywall'" class="mb-4">
							<div>
								<span>{{ trialSubscriptionPromptMessage }}</span>
							Please
							<router-link
								:to="stream.type === 'scheduled' ? '/subscribe?category=restream' : `/subscribe?category=${stream.type}`">
								Upgrade
							</router-link>
							to extend your service.
							</div>
						</Alert>
						<router-view v-slot="{ Component }">
							<component :is="Component" :stream="stream" :primary-stream-alive="primaryStreamAlive"
								:backup-stream-alive="backupStreamAlive" :stream-alive="streamAlive" :media-pulse="mediaPulse"
								:media-pulse-primary="mediaPulsePrimary" :media-pulse-backup="mediaPulseBackup"
								:linked-source="linkedSource"
								:is-vod-trial="isVodTrial" :trial-subscription="trialSubscription"
								:vod-sub-expired-days="vodTrialExpiredIn" @stream-updated="onStreamUpdates"
								@refresh-stream="fetchStreamDetails" @video-update="onVideoFileUpdate"
								@video-removed="onVideoFileRemoved" @set-live-stream-countdown="liveStreamCountdownSelected"
								@live-stream-abr-changed="liveStreamAbrChanged" @dvr-recording-toggle="dvrRecordingToggle"
								@backup-toggle="onBackupToggled" :stream-dvr-enabled="streamDvrEnabled" />
						</router-view>
						<Spinner v-if="isVideoListLoading" text="Retrieving data..." classes="text-surface-8 my-3"
							spinner-color="var(--c-surface-8)" spinner-size="15px" />
					</div>
				</div>

			</main>
			<aside :class="[streamType === 'scheduled' ? 'contents md:block md:order-1 row-start-2 md:self-start md:sticky md:top-4' : 'order-none md:order-1 row-start-1 row-span-2', 'col-start-2 max-w-full w-full', `sidebar ${streamType}_sidebar`]">
				<!-- Scheduled streams: stream control + destinations in sidebar -->
				<template v-if="streamType === 'scheduled'">
					<stream-scheduling-configurator class="order-1 md:order-none w-full" :stream="stream" :stream-alive="streamAlive" :media-pulse="mediaPulse"
						:video-files="videoFiles" :video-files-loaded="videoFilesLoaded" @scheduler-settings-updated="onSchedulerSettingsUpdated"
						@refresh-stream="fetchStreamDetails" @start-stream="showStreamToggleModal" />
					<div class="order-3 md:order-none mt-4 w-full">
						<div class="bg-surface-2 rounded-xl shadow-[0_2px_6px_rgba(0,0,0,0.28)] p-4 md:p-5">
							<platform-list :stream="stream" :media-pulse="mediaPulse"
								@platform-list="onStreamUpdates" @refresh-stream="fetchStreamDetails" />
						</div>
					</div>
				</template>
				<!-- Non-scheduled streams: original sidebar -->
				<stream-sidebar v-if="streamType !== 'scheduled'" :stream="stream" ref="sidebarStreamPreviewPane"
					:stream-alive="streamAlive" :backup-stream-alive="backupStreamAlive"
					:primary-stream-alive="primaryStreamAlive" :media-pulse="mediaPulse"
					:media-pulse-primary="mediaPulsePrimary" :media-pulse-backup="mediaPulseBackup" :isRtmpPullEnabled="isRtmpPullEnabled"
					@stream-updated="onStreamUpdates" @refresh-stream="fetchStreamDetails" @show-stream-toggle-modal="showStreamToggleModal"
					@linked-source-changed="linkedSource = $event" class="mb-4" />
				<!-- Action buttons for non-scheduled streams only -->
				<div v-if="streamType !== 'scheduled'" :class="streamType + '_action'" class="stream-action-area mt-4">
					<div v-if="statusProcessing">
						<span class="inline-block size-4 align-middle rounded-full border-2 border-surface-6 border-t-transparent animate-spin"></span>
					</div>
					<div v-else>
						<div class="!hidden md:!flex justify-end" :class="{ 'pe-none': !userLoaded }">
							<div class="flex" v-if="userPermissions">
								<!-- `wrap-trigger` is bound rather than always on: a disabled <button>
								     fires no hover events, but the extra wrapper is only worth adding
								     for the chat-only case so no other state changes shape. -->
								<Tooltip :active="chatOnlyLocked || (activeTeam && checkTogglePermission())"
									:wrap-trigger="chatOnlyLocked">
									<FormButton @click="showStreamToggleModal" type="secondary"
										:disabled="(activeTeam && checkTogglePermission()) || toggleProcessing || chatOnlyLocked"
										:label="stream.enabled ? 'Disable' : 'Enable'" class="mr-2" />
										<template #content>
											<span v-html="actionTooltipHtml" />
										</template>
								</Tooltip>
								<Tooltip
									:active="chatOnlyLocked || (activeTeam && checkDelPermission()) || isUsedAsLinkedSourceDestination"
									:wrap-trigger="chatOnlyLocked">
									<form-button isIcon title="Delete stream" type="danger"
										:disabled="(activeTeam && checkDelPermission()) || isUsedAsLinkedSourceDestination || chatOnlyLocked"
										@click="requestStreamDelete">
										<icon-base fill="none">
											<icon-bin />
										</icon-base>
									</form-button>
									<template #content>
											<span v-html="chatOnlyLocked ? chatOnlyTooltip : (isUsedAsLinkedSourceDestination ? tooltipUsedAsDestinationText : tooltipText)" />
										</template>
								</Tooltip>
							</div>
							<div class="flex" v-else>
								<FormButton type="secondary" :label="stream.enabled ? 'Disable' : 'Enable'" :disabled="toggleProcessing"
									class="mr-2" @click="showStreamToggleModal" />
								<Tooltip :content="isUsedAsLinkedSourceDestination ? tooltipUsedAsDestinationText : tooltipLinkedSourceText"
									:active="hasEnabledLinkedSources || isUsedAsLinkedSourceDestination">
									<form-button isIcon title="Delete stream" type="danger"
										@click="requestStreamDelete"
										:disabled="hasEnabledLinkedSources || isUsedAsLinkedSourceDestination">
										<icon-base fill="none">
											<icon-bin />
										</icon-base>
									</form-button>
								</Tooltip>
							</div>
						</div>
					</div>
				</div>
			</aside>
		</div>
		<add-stream-modal @new-channel="onNewStream" />
		<confirm-modal message="Delete this stream?"
			body="This permanently deletes the stream and all of its content, including recordings and settings. This action can’t be undone."
			modal-type="danger" ok-text="Delete" cancel-text="Cancel" @modal-confirm="onStreamDeleteConfirm" />

		<confirm-modal message="Please update your subscription to get more concurrent streams." modal-id="increase-limit"
			modal-type="danger" ok-text="Yes" cancel-text="Cancel" @modal-confirm="gotoUpgrade" />
		<confirm-modal
			message="Attention: Multistream has been discontinued. Set up a new Live Stream today for uninterrupted streaming with enhanced features, at no extra cost."
			modal-id="create-aio" ok-text="Create Live Stream" cancel-text="Cancel" @modal-confirm="openCreatestream" />
		<confirm-modal modalId="delete-vod-stream" message="Permanently delete all files in this folder?"
			modal-type="danger" body="Yours files in VOD section inside the stream will also be deleted."
			@modal-confirm="onStreamDeleteConfirm" />
		<confirm-modal modalId="delete-vod-stream-showcase" message="Permanently delete all files in this folder?"
			modal-type="danger"
			body="This folder contains video(s) which are selected in the showcase. Deleting the folder will remove videos from the showcase as well."
			@modal-confirm="onStreamDeleteConfirm" />

		<confirm-modal modal-size="sm" modal-id="scheduled-status-warning" modal-type="danger"
			message="Stream is already scheduled" ok-text="Disable anyway" cancel-text="Cancel"
			body="If you disable the stream, it won't start at the scheduled time."
			@modal-confirm="(scheduledAlert = false); toggleStatus()" />

		<alert-modal modal-id="alert-chat-down"
			message="We have encountered unusual behavior in our chat app and would appreciate your patience while our team gets things back online."
			ok-text="I Understand" class="text-center" />

		<toggle-stream-modal v-if="stream" :stream="stream" @modal-toggle="toggleStatus"
			@toggle-processing="onToggleProcessing" @toggle-done="onToggleDone" />
	</div>
</template>

<style scoped>
.vod_wrap .content {
	max-width: 100%;
	flex: 0 0 100%;
}

:deep(.content .page-title) {
	resize: none;
}

.no-outline {
	outline: none;
}

/* Keep the tab bar horizontally scrollable on narrow screens, but hide the scrollbar */
.no-scrollbar::-webkit-scrollbar {
	display: none;
}

.no-scrollbar {
	-ms-overflow-style: none;
	scrollbar-width: none;
}

/* Drag-to-scroll affordance for the tab bar (desktop mouse) */
.tab-draggable {
	cursor: grab;
}

.tab-draggable.tab-dragging {
	cursor: grabbing;
	user-select: none;
}

@media (max-width: 767px) {
	:deep(.vod-panes .dropdown) {
		width: 100%;
	}

	.vod-nav {
		margin-right: 48px;
	}
}
</style>
